// Global settings defaults + explicit-override tracking.
//
// Value layers:
//   shipped  (code constants, `SettingDef.shipped`)
//   global   (this document, published by an admin; also carries visibility)
//   user     (SettingsStore / localStorage)
//
// A setting with no explicit user override follows the global default and then
// the shipped default. Resetting clears the explicit marker, so later global
// default changes still reach the user.

import { api } from "../base";
import type { Role } from "../admin";
import { canSeeSetting, normalizeAccess, type SettingAccess } from "./access";
import {
  SETTINGS,
  SETTINGS_BY_ID,
  getSettingValue,
  setSettingValue,
  type SettingDef,
} from "./registry";

const GLOBAL_KEY = "webrace.settings.defaults";
const EXPLICIT_KEY = "webrace.settings.explicit";
const EXPLICIT_VERSION_KEY = "webrace.settings.explicit.version";

/**
 * Bump when new setting ids are added to the registry. On the first load after
 * an upgrade, new ids are compared with the shipped value so existing custom
 * rules/binds/sounds/mode values are preserved instead of being overwritten by
 * the new defaults.
 */
const REGISTRY_VERSION = 2;

export interface GlobalDefaultsDoc {
  v: 1;
  values: Record<string, unknown>;
  access: Record<string, SettingAccess>;
}

function clone<T>(value: T): T {
  if (value === null || typeof value !== "object") return value;
  return JSON.parse(JSON.stringify(value)) as T;
}

function equal(a: unknown, b: unknown): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

export function loadCachedGlobalDefaults(): GlobalDefaultsDoc {
  try {
    const raw = localStorage.getItem(GLOBAL_KEY);
    if (!raw) return { v: 1, values: {}, access: {} };
    const parsed = JSON.parse(raw) as Partial<GlobalDefaultsDoc>;
    return normalizeGlobalDefaults(parsed);
  } catch {
    return { v: 1, values: {}, access: {} };
  }
}

export function normalizeGlobalDefaults(raw: unknown): GlobalDefaultsDoc {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const values: Record<string, unknown> = {};
  const access: Record<string, SettingAccess> = {};
  if (o.values && typeof o.values === "object") {
    for (const [key, value] of Object.entries(o.values as Record<string, unknown>)) {
      if (SETTINGS_BY_ID.has(key)) values[key] = value;
    }
  }
  if (o.access && typeof o.access === "object") {
    for (const [key, value] of Object.entries(o.access as Record<string, unknown>)) {
      if (SETTINGS_BY_ID.has(key)) access[key] = normalizeAccess(value);
    }
  }
  return { v: 1, values, access };
}

function cacheGlobalDefaults(doc: GlobalDefaultsDoc): void {
  try {
    localStorage.setItem(GLOBAL_KEY, JSON.stringify(doc));
  } catch {
    /* ignore */
  }
}

/** Fetch the published defaults (falls back to the cached copy on failure). */
export async function fetchGlobalDefaults(): Promise<GlobalDefaultsDoc> {
  try {
    const r = await fetch(api("/api/settings/defaults"), { cache: "no-store" });
    if (!r.ok) return loadCachedGlobalDefaults();
    const body = (await r.json()) as { defaults?: unknown };
    const doc = normalizeGlobalDefaults(body.defaults ?? body);
    cacheGlobalDefaults(doc);
    return doc;
  } catch {
    return loadCachedGlobalDefaults();
  }
}

/** Admin-only read (the server validates the token; never used by clients). */
export async function fetchAdminGlobalDefaults(
  token: string,
): Promise<{ ok: true; doc: GlobalDefaultsDoc; updatedAt: string | null } | { ok: false; error: string }> {
  try {
    const r = await fetch(api("/api/settings/admin"), {
      cache: "no-store",
      headers: token ? { "x-admin-token": token } : {},
    });
    if (!r.ok) {
      const body = (await r.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: body.error ?? `HTTP ${r.status}` };
    }
    const body = (await r.json()) as { defaults?: unknown; updatedAt?: string | null };
    return { ok: true, doc: normalizeGlobalDefaults(body.defaults), updatedAt: body.updatedAt ?? null };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

export async function publishGlobalDefaults(
  doc: GlobalDefaultsDoc,
  token: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const r = await fetch(api("/api/settings/defaults"), {
      method: "PUT",
      headers: { "content-type": "application/json", "x-admin-token": token },
      body: JSON.stringify(doc),
    });
    if (!r.ok) {
      const body = (await r.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: body.error ?? `HTTP ${r.status}` };
    }
    cacheGlobalDefaults(doc);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

export function accessFor(def: SettingDef, doc: GlobalDefaultsDoc): SettingAccess {
  return doc.access[def.id] ?? def.access;
}

export function effectiveValue(def: SettingDef, doc = loadCachedGlobalDefaults()): unknown {
  const value = def.id in doc.values ? doc.values[def.id] : def.shipped;
  return clone(value);
}

/** Settings visible to a role (hidden "none" entries are excluded). */
export function visibleSettings(role: Role, doc = loadCachedGlobalDefaults()): SettingDef[] {
  return SETTINGS.filter((def) => canSeeSetting(accessFor(def, doc), role));
}

// ---- Explicit user overrides --------------------------------------------

type ExplicitMap = Record<string, true>;

function readExplicit(): ExplicitMap {
  try {
    const raw = localStorage.getItem(EXPLICIT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const out: ExplicitMap = {};
    for (const key of Object.keys(parsed as Record<string, unknown>)) {
      if (SETTINGS_BY_ID.has(key)) out[key] = true;
    }
    return out;
  } catch {
    return {};
  }
}

function writeExplicit(map: ExplicitMap): void {
  try {
    localStorage.setItem(EXPLICIT_KEY, JSON.stringify(map));
    localStorage.setItem(EXPLICIT_VERSION_KEY, String(REGISTRY_VERSION));
  } catch {
    /* ignore */
  }
}

/**
 * Build the explicit map once for an existing install. Values that differ from
 * the shipped baseline are treated as deliberate user overrides, so the first
 * global-default rollout never clobbers a player's saved setup.
 */
export function ensureExplicitMap(settings: unknown): void {
  const hasMap = localStorage.getItem(EXPLICIT_KEY) !== null;
  const version = Number(localStorage.getItem(EXPLICIT_VERSION_KEY) ?? "0");
  if (hasMap && version >= REGISTRY_VERSION) return;
  const map: ExplicitMap = hasMap ? readExplicit() : {};
  for (const def of SETTINGS) {
    if (!def.path && !def.get) continue;
    if (map[def.id]) continue;
    const current = getSettingValue(def, settings);
    // Missing optional values (sound assignments with no shipped file) are not
    // user overrides; they should follow a global default when one is added.
    if (current !== undefined && !equal(current, def.shipped)) map[def.id] = true;
  }
  writeExplicit(map);
}

export function isExplicit(id: string): boolean {
  return readExplicit()[id] === true;
}

export function markExplicit(id: string): void {
  const map = readExplicit();
  if (map[id]) return;
  map[id] = true;
  writeExplicit(map);
}

export function clearExplicit(id: string): void {
  const map = readExplicit();
  if (!map[id]) return;
  delete map[id];
  writeExplicit(map);
}

/** Forget every personal override (used by “reset all settings”). */
export function clearAllExplicit(): void {
  writeExplicit({});
}

/**
 * Overlay global defaults onto a loaded settings object. Explicit user values
 * win; unset values follow the global default, then the shipped baseline.
 */
export function applyGlobalDefaults<T>(settings: T, doc = loadCachedGlobalDefaults()): T {
  ensureExplicitMap(settings);
  const explicit = readExplicit();
  for (const def of SETTINGS) {
    if ((!def.path && !def.set) || explicit[def.id]) continue;
    setSettingValue(def, settings, effectiveValue(def, doc));
  }
  return settings;
}

/** Reset one setting to its effective default and clear the explicit marker. */
export function resetSetting(settings: unknown, def: SettingDef, doc = loadCachedGlobalDefaults()): void {
  clearExplicit(def.id);
  if (def.path || def.set) setSettingValue(def, settings, effectiveValue(def, doc));
}

/** True when the current value equals the effective default. */
export function atDefault(settings: unknown, def: SettingDef, doc = loadCachedGlobalDefaults()): boolean {
  if (!def.path && !def.get) return true;
  const current = getSettingValue(def, settings);
  if (current === undefined) return true;
  return equal(current, effectiveValue(def, doc));
}
